import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface FieldProps {
  id?: string;
  /** Optional: drop it when the card heading already says the same
   *  thing, rather than repeating it a line below in smaller type. */
  label?: string;
  hint?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Tiny vertical form field wrapper — `<label>` + control + helper hint.
 *
 * Editors compose dozens of these so this trims a lot of repeated
 * markup. The label class mirrors `.label` from `index.css`.
 */
export function Field({
  id,
  label,
  hint,
  required,
  className,
  children,
}: FieldProps): JSX.Element {
  return (
    <div className={cn("space-y-1", className)}>
      {label !== undefined && (
        <label htmlFor={id} className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500">
          {label}
          {required && <span className="ml-0.5 text-error-dark">*</span>}
        </label>
      )}
      {children}
      {hint && <p className="text-xs text-neutral-500">{hint}</p>}
    </div>
  );
}

interface ChipsInputProps {
  value: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Suggestions surfaced as `<datalist>` options. */
  suggestions?: ReadonlyArray<string>;
  id?: string;
}

/**
 * Inline chip editor for short string lists (`focus_areas`,
 * `ignore_filters`, etc.).
 *
 * Empty state shows the placeholder; pressing Enter or comma adds a
 * chip; Backspace on an empty input removes the last chip.
 */
export function ChipsInput({
  value,
  onChange,
  placeholder = "Type and press Enter…",
  suggestions,
  id,
}: ChipsInputProps): JSX.Element {
  const listId = `chips-${id ?? "x"}`;

  const handleAdd = (raw: string): void => {
    const text = raw.trim();
    if (!text) return;
    if (value.includes(text)) return;
    onChange([...value, text]);
  };

  const handleRemove = (text: string): void => {
    onChange(value.filter((v) => v !== text));
  };

  return (
    <div className="rounded-md border border-neutral-300 bg-white px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-1">
        {value.map((chip) => (
          <span
            key={chip}
            className="inline-flex items-center gap-1 rounded-full bg-primary-line px-2 py-0.5 text-xs font-medium text-primary-ink"
          >
            {chip}
            <button
              type="button"
              onClick={() => handleRemove(chip)}
              className="text-primary hover:text-primary-ink"
              aria-label={`Remove ${chip}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={id}
          list={suggestions ? listId : undefined}
          placeholder={value.length === 0 ? placeholder : ""}
          onKeyDown={(e) => {
            const target = e.currentTarget;
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              handleAdd(target.value);
              target.value = "";
            } else if (
              e.key === "Backspace" &&
              !target.value &&
              value.length > 0
            ) {
              const last = value[value.length - 1];
              if (last !== undefined) handleRemove(last);
            }
          }}
          className="min-w-[6ch] flex-1 bg-transparent text-xs outline-none"
        />
      </div>
      {suggestions && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
    </div>
  );
}
